// Minimal TON address form converter (raw "workchain:hex64" ⇄ friendly).
//
// This exists so display-only code (wallet-tracker notifications, the deposit
// address in ExchangeModal) does not have to import @ton/core — the full TON
// stack is ~400KB minified and must stay out of the boot chunk. The byte
// format is the stable TON friendly address wire format (tag 0x11 bounceable /
// 0x51 non-bounceable, workchain int8, 32-byte hash, CRC16-XMODEM checksum,
// base64url) and tonRawAddress.test.ts pins byte-for-byte equivalence against
// @ton/core's Address.parse / Address.toString().

function crc16Xmodem(data: Uint8Array): number {
  let crc = 0
  for (const byte of data) {
    crc ^= byte << 8
    for (let bit = 0; bit < 8; bit++) {
      crc = crc & 0x8000 ? ((crc << 1) ^ 0x1021) & 0xffff : (crc << 1) & 0xffff
    }
  }
  return crc
}

function base64UrlEncode(data: Uint8Array): string {
  let binary = ''
  for (const byte of data) binary += String.fromCharCode(byte)
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function base64UrlDecode(text: string): Uint8Array | null {
  const padded = text.replace(/-/g, '+').replace(/_/g, '/')
  if (!/^[A-Za-z0-9+/]*$/.test(padded)) return null
  try {
    const binary = atob(padded)
    const out = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i)
    return out
  } catch {
    return null
  }
}

interface AddressParts {
  workchain: number
  hash: Uint8Array
}

function parseRawAddress(raw: string): AddressParts | null {
  const match = /^(-?\d+):([0-9a-fA-F]{64})$/.exec(raw.trim())
  const workchainRaw = match?.[1]
  const hex = match?.[2]
  if (!workchainRaw || !hex) return null
  const workchain = Number(workchainRaw)
  if (!Number.isInteger(workchain) || workchain < -128 || workchain > 127) return null
  const hash = new Uint8Array(32)
  for (let i = 0; i < 32; i++) {
    hash[i] = Number.parseInt(hex.slice(i * 2, i * 2 + 2), 16)
  }
  return { workchain, hash }
}

function parseFriendlyAddress(text: string): AddressParts | null {
  if (text.length !== 48) return null
  const bytes = base64UrlDecode(text)
  if (!bytes || bytes.length !== 36) return null
  const tag = (bytes[0] ?? 0) & 0x7f
  if (tag !== 0x11 && tag !== 0x51) return null
  const payload = bytes.subarray(0, 34)
  const crc = crc16Xmodem(payload)
  if (bytes[34] !== crc >> 8 || bytes[35] !== (crc & 0xff)) return null
  const workchainByte = bytes[1] ?? 0
  const workchain = workchainByte > 127 ? workchainByte - 256 : workchainByte
  return { workchain, hash: bytes.slice(2, 34) }
}

function encodeFriendly(parts: AddressParts, bounceable: boolean): string {
  const payload = new Uint8Array(34)
  payload[0] = bounceable ? 0x11 : 0x51
  payload[1] = parts.workchain & 0xff
  payload.set(parts.hash, 2)
  const crc = crc16Xmodem(payload)
  const out = new Uint8Array(36)
  out.set(payload)
  out[34] = crc >> 8
  out[35] = crc & 0xff
  return base64UrlEncode(out)
}

/** Returns the friendly bounceable form of a raw "workchain:hex64" address, or
    null when the input is not in raw form (caller falls back to the input). */
export function rawAddressToFriendlyBounceable(raw: string): string | null {
  const parts = parseRawAddress(raw)
  return parts ? encodeFriendly(parts, true) : null
}

/** Canonical friendly form of a raw OR friendly address with the requested
    bounceable flag (equivalent to
    `Address.parse(input).toString({ bounceable, urlSafe: true })`), or null
    when the input is not a recognizable TON address. */
export function addressToFriendlyForm(input: string, bounceable: boolean): string | null {
  const parts = parseRawAddress(input) ?? parseFriendlyAddress(input.trim())
  return parts ? encodeFriendly(parts, bounceable) : null
}

/** `workchain:hex64` form, or null when the input is not a TON address. */
export function addressToRawForm(input: string): string | null {
  const parts = parseRawAddress(input.trim()) ?? parseFriendlyAddress(input.trim())
  if (!parts) return null
  const hex = Array.from(parts.hash, (byte) => byte.toString(16).padStart(2, '0')).join('')
  return `${parts.workchain}:${hex}`
}
